import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  if (sent) return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md"><CardContent className="p-8 text-center space-y-3">
        <h2 className="font-outfit text-xl font-bold">Check Your Email</h2>
        <p className="text-sm text-muted-foreground">If an account with that email exists, we've sent a reset link. Check your inbox.</p>
        <Button variant="outline" onClick={() => navigate("/login")}>Back to Login</Button>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md" data-testid="forgot-password-card">
        <CardHeader><CardTitle className="font-outfit text-xl">Forgot Password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">Enter the email address associated with your account.</p>
            <Input data-testid="forgot-email" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button data-testid="forgot-submit" type="submit" className="w-full" disabled={loading}>{loading ? "Sending..." : "Send Reset Link"}</Button>
            <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={() => navigate("/login")}>Back to login</button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (password.length < 6) { toast.error("At least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset!");
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md"><CardContent className="p-8 text-center space-y-3">
        <h2 className="font-outfit text-xl font-bold">Password Reset!</h2>
        <p className="text-sm text-muted-foreground">You can now log in with your new password.</p>
        <Button onClick={() => navigate("/login")}>Go to Login</Button>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md" data-testid="reset-password-card">
        <CardHeader><CardTitle className="font-outfit text-xl">Reset Password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input data-testid="reset-password" type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Input data-testid="reset-confirm" type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            <Button data-testid="reset-submit" type="submit" className="w-full" disabled={loading}>{loading ? "Resetting..." : "Reset Password"}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
